import DOMPurify from "dompurify";

/**
 * Strips executable content out of a message body before it is rendered.
 *
 * The bodies that reach the chat log are written by strangers: a `mail_received`
 * or `message_received` row holds whatever a third party sent to the customer's
 * mailbox or LinkedIn inbox. They are assigned to `innerHTML`, and while
 * `<script>` does not run when inserted that way, event-handler attributes do —
 * `<img src=x onerror="…">` executes in the app's own origin, where the
 * Signalist JWT is reachable. Opening the Inbox is the whole exploit (PREC-3957).
 *
 * The shadow root these bodies land in is a **style** boundary, not a security
 * one: script inside a shadow tree has the page's full privileges.
 *
 * The configuration is deliberately permissive about presentation, because the
 * other failure mode is silent: over-sanitizing does not throw, it just makes
 * customers' mail render wrong. Marketing e-mail is built out of tables, inline
 * `style`, `<style>` blocks, images and links, so all of those stay — the shadow
 * root is what keeps a body's CSS from leaking into the app. What goes is
 * anything that executes or navigates on its own: scripts, event handlers,
 * embedded documents, form posts, and `javascript:` URLs (DOMPurify drops those
 * last three categories of attribute value by default).
 */
const SANITIZE_CONFIG = {
	// Keep the <style> blocks e-mail templates rely on; the shadow root scopes them.
	ADD_TAGS: ["style"],
	// A leading <style> is hoisted into <head> by the parser; without this DOMPurify
	// returns the body only and the block disappears.
	FORCE_BODY: true,
	// Preserved so splitEmailHistory's target="_blank" / rel rewriting survives.
	ADD_ATTR: ["target", "rel"],
	// Embedded documents, form posts and document-level tags have no place in a
	// message body and are each a way to execute or exfiltrate.
	FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "base", "link", "meta"],
	FORBID_ATTR: ["srcdoc", "formaction", "action", "ping"],
};

/**
 * Flattens a whole-document body into a fragment, so its <head> survives.
 *
 * Real inbound mail is not a fragment — it is `<html><head><style>…</head><body>`,
 * and that is where its CSS lives (1161 of 1890 sampled prod bodies carry a
 * <style> block). Sanitizing such a document returns the BODY only, silently
 * dropping every block: the three prod bodies sampled lost 5, 2 and 1 of them,
 * and the mail renders unstyled. Nothing throws, which is why the synthetic
 * fragment tests all passed while real mail broke.
 *
 * `DOMParser` is inert — it runs no script and loads no resource — so parsing
 * hostile input here is safe; the result is handed to DOMPurify below regardless.
 */
function flattenDocumentToFragment(html: string): string {
	const doc = new DOMParser().parseFromString(html, "text/html");

	unwrapCommentedCss(doc);

	return `${doc.head.innerHTML}${doc.body.innerHTML}`;
}

/**
 * Unwraps the legacy `<style><!-- … --></style>` idiom.
 *
 * Mail clients (Outlook especially) still wrap stylesheet contents in an HTML
 * comment — an idiom for hiding CSS from browsers that predate <style>. DOMPurify
 * drops any <style> containing a comment outright, because a comment inside CSS is
 * an mXSS vector, so those blocks vanished and the mail rendered unstyled.
 *
 * Removing only the wrapper markers turns it back into ordinary CSS, which is then
 * handed to DOMPurify like everything else — this relaxes nothing, it just stops a
 * 1990s formatting quirk from costing the customer their e-mail's styling.
 */
function unwrapCommentedCss(doc: Document): void {
	for (const style of Array.from(doc.querySelectorAll("style"))) {
		const css = style.textContent ?? "";
		const unwrapped = css.replace(/^\s*<!--/, "").replace(/-->\s*$/, "");

		if (unwrapped !== css) {
			style.textContent = unwrapped;
		}
	}
}

/** Returns `html` with every executable construct removed, safe to assign to innerHTML. */
export function sanitizeMessageHtml(html: string): string {
	if (!html) {
		return "";
	}

	return DOMPurify.sanitize(flattenDocumentToFragment(html), SANITIZE_CONFIG);
}
